/**
 * ColorSourceAnalysis - Color source analysis display
 *
 * Shows mana sources per color with dynamic turn-based recommendations
 * using hypergeometric probability, visual indicators (✓/⚠️/✗), and a
 * turn slider (T1–T10) for browsing different target turns.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';

import InfoButton, { InfoList, InfoSection, InfoTip, InfoWarning } from '@/components/InfoButton/InfoButton';
import type { Color } from '@/types/card';
import type { DeckFormat } from '@/types/deck';

import type { ColorSourceAnalysis as ColorSourceAnalysisData } from '../../hooks/useManaBaseAnalysis';
import { getDefaultTurn } from '../../lib/manamath';

export interface ColorSourceAnalysisProps {
  data: ColorSourceAnalysisData;
  /** Compact mode for left panel */
  compact?: boolean;
  /** Deck format for default turn calculation */
  format?: DeckFormat | null;
  /** Current target turn (undefined = format default) */
  targetTurn?: number;
  /** Callback when user changes the target turn slider */
  onTurnChange?: (turn: number) => void;
}

/** MTG mana colors for styling */
const MANA_COLORS: Record<Color, { bg: string; text: string; border: string }> = {
  W: { bg: 'bg-amber-50', text: 'text-amber-900', border: 'border-amber-200' },
  U: { bg: 'bg-blue-600', text: 'text-blue-50', border: 'border-blue-400' },
  B: { bg: 'bg-zinc-700', text: 'text-zinc-100', border: 'border-zinc-500' },
  R: { bg: 'bg-red-600', text: 'text-red-50', border: 'border-red-400' },
  G: { bg: 'bg-green-700', text: 'text-green-50', border: 'border-green-500' },
};

const COLOR_NAMES: Record<Color, string> = {
  W: 'White',
  U: 'Blue',
  B: 'Black',
  R: 'Red',
  G: 'Green',
};

const STATUS_ICONS: Record<'good' | 'warning' | 'critical', string> = {
  good: '✓',
  warning: '⚠️',
  critical: '✗',
};

const STATUS_COLORS = {
  good: 'text-emerald-400',
  warning: 'text-amber-400',
  critical: 'text-red-400',
};

function ColorSourceInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          This analysis tells you if you have enough mana sources of each color to reliably cast your spells. It uses{' '}
          <strong>hypergeometric probability</strong> (based on Frank Karsten&apos;s mana base research) to calculate how many sources you
          need to hit your colors by the selected turn with 90% reliability.
        </p>
      </InfoSection>

      <InfoSection title="How it works">
        <p className="mb-2">Use the turn slider to see how many sources you need at different points in the game:</p>
        <InfoList
          items={[
            'Earlier turns require more sources (harder to find colors)',
            'Later turns require fewer sources (more draws to find them)',
            'The calculation adapts to your deck size automatically',
          ]}
        />
        <p className="mt-2 text-xs opacity-80">Sources include lands that produce the color, fetch lands, mana dorks, and rocks.</p>
      </InfoSection>

      <InfoSection title="Reading the indicators">
        <InfoList
          items={[
            '✓ Green: You have enough sources',
            '⚠️ Yellow: Slightly below recommended (1-3 short)',
            '✗ Red: Significantly below recommended (4+ short)',
          ]}
        />
      </InfoSection>

      <InfoSection title="The Health Score">
        <p>
          The percentage is a quick summary of your color reliability. It starts at 100% and decreases based on deficiencies:{' '}
          <strong>-15%</strong> for each yellow warning, <strong>-30%</strong> for each red critical.
        </p>
      </InfoSection>

      <InfoTip>
        Focus on fixing red (critical) colors first — those will cause the most mulligans and color screw. Dual lands and fetch lands that
        produce multiple colors are the best way to improve multiple deficiencies at once.
      </InfoTip>

      <InfoWarning>
        A deck with 40% health means you&apos;ll frequently struggle to cast your spells on time. Add more lands or mana sources that
        produce your weak colors.
      </InfoWarning>
    </>
  );
}

export default function ColorSourceAnalysis({
  data,
  compact = true,
  format,
  targetTurn,
  onTurnChange,
}: ColorSourceAnalysisProps): ReactElement {
  const { colors, healthScore } = data;
  const currentTurn = targetTurn ?? getDefaultTurn(format ?? null);

  // No colors in deck
  if (colors.length === 0) {
    return (
      <div className="text-ash flex h-20 items-center justify-center text-sm">
        <span>No colored spells</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Turn slider */}
      {onTurnChange && (
        <div className="rounded-lg bg-zinc-800/30 p-2">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-ash text-xs">Target Turn</span>
            <span className="text-silver text-xs font-semibold">T{currentTurn}</span>
          </div>
          <input
            type="range"
            min={1}
            max={10}
            step={1}
            value={currentTurn}
            onChange={(e) => onTurnChange(Number(e.target.value))}
            className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-zinc-700 accent-violet-500 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-violet-500"
            aria-label="Target turn for mana analysis"
          />
          <div className="text-ash mt-0.5 flex justify-between text-[9px]">
            <span>T1</span>
            <span>T10</span>
          </div>
        </div>
      )}

      {/* Health score bar */}
      <div className="rounded-lg bg-zinc-800/30 p-2">
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-ash text-xs">Mana Base Health</span>
            <InfoButton title="Color Source Analysis">
              <ColorSourceInfo />
            </InfoButton>
          </div>
          <span
            className={clsx(
              'text-sm font-semibold',
              healthScore >= 80 ? 'text-emerald-400' : healthScore >= 50 ? 'text-amber-400' : 'text-red-400',
            )}
          >
            {healthScore}%
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-zinc-700">
          <div
            className={clsx(
              'h-full transition-all duration-300',
              healthScore >= 80 ? 'bg-emerald-500' : healthScore >= 50 ? 'bg-amber-500' : 'bg-red-500',
            )}
            style={{ width: `${healthScore}%` }}
          />
        </div>
      </div>

      {/* Color source rows */}
      <div className="space-y-2">
        {colors.map((colorData) => (
          <div key={colorData.color} className="rounded-lg bg-zinc-800/20 p-2">
            <div className="flex items-center gap-2">
              {/* Color pip indicator */}
              <div
                className={clsx(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold',
                  MANA_COLORS[colorData.color].bg,
                  MANA_COLORS[colorData.color].text,
                  MANA_COLORS[colorData.color].border,
                )}
              >
                {colorData.color}
              </div>

              {/* Color name and source count */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-silver text-sm font-medium">{compact ? colorData.color : COLOR_NAMES[colorData.color]}</span>
                  <div className="flex items-center gap-1.5">
                    <span className={clsx('text-xs', STATUS_COLORS[colorData.status])}>{STATUS_ICONS[colorData.status]}</span>
                    <span className={clsx('text-sm font-semibold', STATUS_COLORS[colorData.status])}>
                      {colorData.sources}/{colorData.recommended}
                    </span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-zinc-700">
                  <div
                    className={clsx(
                      'h-full transition-all duration-300',
                      colorData.status === 'good' ? 'bg-emerald-500' : colorData.status === 'warning' ? 'bg-amber-500' : 'bg-red-500',
                    )}
                    style={{ width: `${Math.min(100, (colorData.sources / colorData.recommended) * 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Suggestion */}
            {colorData.suggestion && (
              <p className={clsx('mt-1.5 text-xs', STATUS_COLORS[colorData.status], 'opacity-80')}>{colorData.suggestion}</p>
            )}
          </div>
        ))}
      </div>

      {/* Legend (compact mode) */}
      {compact && <div className="text-ash text-center text-[10px]">Sources / Recommended by Turn {currentTurn} (90% reliability)</div>}
    </div>
  );
}
